import type { CommentPermission, MapComment } from '../types';

type ApiErrorPayload = { message?: string };

export class CommentApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'CommentApiError';
    this.status = status;
  }
}

type CommentListResponse = {
  success?: boolean;
  message?: string;
  permission?: CommentPermission;
  comments?: MapComment[];
};

const apiBase = (): string => process.env.REACT_APP_API_BASE_URL ?? '';

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const readPayload = async <T>(response: Response): Promise<T> => {
  const payload = (await response.json().catch(() => ({}))) as T & ApiErrorPayload;
  if (!response.ok || (typeof payload === 'object' && payload !== null && 'success' in payload && payload.success === false)) {
    throw new CommentApiError(response.status, payload.message ?? 'Unable to update comments');
  }
  return payload;
};

export const listMapComments = async (
  fileId: number,
): Promise<{ comments: MapComment[]; permission: CommentPermission }> => {
  const response = await fetch(`${apiBase()}/api/comments/list`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId }),
  });
  const payload = await readPayload<CommentListResponse>(response);
  return {
    comments: Array.isArray(payload.comments) ? payload.comments : [],
    permission: payload.permission ?? {
      canViewComments: false,
      canManageComments: false,
      isOwner: false,
    },
  };
};

export type CommentDraft = {
  fileId: number;
  segmentId?: number | null;
  anchorType: 'race_time' | 'map_point';
  anchorUtc?: string | null;
  anchorLat: number;
  anchorLon: number;
  body: string;
  attachments: File[];
};

const appendDraft = (formData: FormData, draft: CommentDraft): void => {
  formData.append('fileId', String(draft.fileId));
  if (draft.segmentId) formData.append('segmentId', String(draft.segmentId));
  formData.append('anchorType', draft.anchorType);
  if (draft.anchorUtc) formData.append('anchorUtc', draft.anchorUtc);
  formData.append('anchorLat', String(draft.anchorLat));
  formData.append('anchorLon', String(draft.anchorLon));
  formData.append('body', draft.body);
  for (const attachment of draft.attachments) {
    formData.append('attachments', attachment);
  }
};

export const createMapComment = async (draft: CommentDraft): Promise<MapComment> => {
  const formData = new FormData();
  appendDraft(formData, draft);
  const response = await fetch(`${apiBase()}/api/comments/create`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  const payload = await readPayload<{ comment?: MapComment }>(response);
  if (!payload.comment) throw new CommentApiError(response.status, 'Comment was not returned');
  return payload.comment;
};

export const updateMapComment = async ({
  id,
  version,
  body,
  anchor,
  attachments,
  removeAttachmentIds,
}: {
  id: number;
  version: number;
  body: string;
  anchor?: Pick<CommentDraft, 'anchorType' | 'anchorUtc' | 'anchorLat' | 'anchorLon'>;
  attachments: File[];
  removeAttachmentIds: number[];
}): Promise<MapComment> => {
  const formData = new FormData();
  formData.append('version', String(version));
  formData.append('body', body);
  if (anchor) {
    formData.append('anchorType', anchor.anchorType);
    if (anchor.anchorUtc) formData.append('anchorUtc', anchor.anchorUtc);
    formData.append('anchorLat', String(anchor.anchorLat));
    formData.append('anchorLon', String(anchor.anchorLon));
  }
  formData.append('removeAttachmentIds', JSON.stringify(removeAttachmentIds));
  for (const attachment of attachments) {
    formData.append('attachments', attachment);
  }
  const response = await fetch(`${apiBase()}/api/comments/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: formData,
  });
  const payload = await readPayload<{ comment?: MapComment }>(response);
  if (!payload.comment) throw new CommentApiError(response.status, 'Comment was not returned');
  return payload.comment;
};

export const deleteMapComment = async (id: number): Promise<void> => {
  const response = await fetch(`${apiBase()}/api/comments/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await readPayload<{ success?: boolean }>(response);
};

export const fetchCommentAttachment = async (url: string): Promise<Blob> => {
  const response = await fetch(`${apiBase()}${url}`, { headers: authHeaders() });
  if (!response.ok) {
    throw new CommentApiError(response.status, 'Unable to load comment image');
  }
  return response.blob();
};

export const fetchCommentAuthorAvatar = async (url: string): Promise<Blob> => {
  const response = await fetch(`${apiBase()}${url}`, { headers: authHeaders() });
  if (!response.ok) {
    throw new CommentApiError(response.status, 'Unable to load comment author avatar');
  }
  return response.blob();
};
